import React from 'react';
import { useIntl } from 'react-intl';

import alertToast from 'Util/ToastifyUtils';
import { usePublishTabEditSession } from 'Modules/UserAccount/Components/TabEditSession';
import { ToggleSwitch } from 'Views/Components/New/ToggleSwitch';
import { AccountDetailsTabHeader } from 'Views/Components/_HumanWritten/AccountDetails/AccountDetailsTabHeader';
import { LottieSpinner } from 'Views/Components/_HumanWritten/Lottie/LottieSpinner';
import MessageBox from 'Views/Components/_HumanWritten/MessageBox/MessageBox';
import { useCommunicationPreferences } from '../Hooks/useCommunicationPreferences';
import { usePreferenceEdits } from '../Hooks/usePreferenceEdits';
import { useUpdateCommunicationPreferences } from '../Hooks/useUpdateCommunicationPreferences';
import PreferenceTable from './PreferenceTable';

/**
 * The customer's own communication preferences: which messages reach them, and on which channel.
 *
 * <p>Edits are staged behind a Save rather than written on each tick. A mis-click would otherwise
 * switch off a message they wanted with nothing to undo it and nothing telling them it happened —
 * and they would only find out by not receiving something.</p>
 *
 * <p>The staff view of the same data is {@link StaffCommunicationPreferences}, which can override
 * more and requires a written reason.</p>
 */
export default function CommunicationPreferencesSection() {
	const intl = useIntl();
	const { data, isPending, isError } = useCommunicationPreferences();
	const update = useUpdateCommunicationPreferences();
	const edits = usePreferenceEdits(data);

	const save = async () => {
		await update.mutateAsync(edits.payload());
		edits.discard();
		alertToast('Preferences saved', 'success');
	};

	// Save and Discard live in the page header, beside the account's own — one place on the screen
	// where a save lives, whichever tab is open. Declared before the early returns below because it is
	// a hook: nothing is published while the data is still loading, which is also correct.
	usePublishTabEditSession(
		edits.hasChanges
			? { saving: update.isPending, onSave: save, onCancel: edits.discard, cancelLabel: 'Discard' }
			: null,
	);

	if (isPending) {
		return <LottieSpinner />;
	}

	if (isError || !data) {
		return (
			<MessageBox status="error" title="Could not load your preferences">
				Refresh the page to try again.
			</MessageBox>
		);
	}

	const saving = update.isPending;

	// The column and the master switch follow the application-wide SMS setting, not whether a template
	// happens to be enabled today. An operator switching templates on one at a time would otherwise
	// make the whole channel appear and disappear from under the customer.
	const offersSms = data.smsEnabled;

	// Every SMS control is inert when the master switch is down or there is no number on file. Shown
	// as off and disabled rather than hidden, so the customer can see what they would get back
	// by switching it on — and switching it back on restores what they had, because the stored rows
	// are untouched either way.
	const smsMuted = !edits.smsMasterValue || !data.hasPhoneNumber;

	return (
		<div className="flex flex-col gap-4">
			<AccountDetailsTabHeader headerText="Communications" />

			{offersSms && !data.hasPhoneNumber && (
				<MessageBox status="info" title="No mobile number on your account">
					Add a mobile number on the Account tab to receive messages by SMS. Until
					then we will email you instead.
				</MessageBox>
			)}

			<p className="text-sm text-gray-600">
				Choose which messages you receive and how they reach you.
			</p>

			<PreferenceTable
				rows={data.rows}
				valueOf={edits.valueOf}
				disabled={saving}
				smsMuted={smsMuted}
				showSms={data.smsEnabled}
				markRecommended
				onChange={edits.setCell}
			/>

			<div className="flex flex-col gap-4 border-t border-gray-200 pt-4">
				{offersSms && (
					<ToggleSwitch
						size="sm"
						label="Receive messages by SMS"
						// Below the table rather than above it: it governs the column beside it, and a
						// switch that greys out seven rows reads better as a footnote to them than as
						// the first thing on the screen.
						description="Turning this off stops every SMS above. Your choices are kept, so turning it back on restores them."
						checked={edits.smsMasterValue}
						disabled={saving || !data.hasPhoneNumber}
						onChange={event => edits.setSmsMaster(event.target.checked)}
						data-test-id="communication-preference-sms-master"
					/>
				)}

				{data.marketingEnabled && (
					<ToggleSwitch
						size="sm"
						// The same wording as the booking wizard's checkbox, through the same language
						// key — it is the same consent, and two names for it on two screens reads as two
						// settings.
						label={intl.formatMessage({
							id: 'booking__marketing-subscription__label',
							defaultMessage: 'Subscribe to marketing communications',
						})}
						description={intl.formatMessage({
							id: 'booking__marketing-subscription__description',
							defaultMessage:
								'Receive promotional offers, schedule changes, and updates from us.',
						})}
						// Never answered reads as off. Switching it on is the first time a value is
						// written, so silence is not counted as consent.
						checked={edits.marketingValue}
						disabled={saving}
						onChange={event => edits.setMarketing(event.target.checked)}
						data-test-id="communication-preference-marketing"
					/>
				)}
			</div>

		</div>
	);
}
